import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, Boxes, Search } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Badge } from '@avagency/frontend/components/ui/badge';
import { Button } from '@avagency/frontend/components/ui/button';
import { Input } from '@avagency/frontend/components/ui/input';
import { Label } from '@avagency/frontend/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avagency/frontend/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@avagency/frontend/components/ui/dialog';
import { EmptyState } from '@avagency/frontend/components/shell/EmptyState';
import { ErrorState } from '@avagency/frontend/components/shell/ErrorState';
import { LoadingState } from '@avagency/frontend/components/shell/LoadingState';
import { cn } from '@avagency/frontend/lib/utils';
import {
  fetchInventory,
  fetchProducts,
  updateProduct,
  type InventoryItem,
} from '../api';

type SortKey = 'sku' | 'on_hand' | 'velocity_7d' | 'reorder_point' | 'status';
type SortDir = 'asc' | 'desc';
type InvStatus = 'ok' | 'low' | 'oos' | 'unconfigured';

// item.reorder_point is omitempty on the Go side → undefined when nil, never null.
function inventoryStatus(item: InventoryItem): InvStatus {
  if (!item.is_configured || item.reorder_point == null) return 'unconfigured';
  if (item.on_hand === 0) return 'oos';
  if (item.on_hand <= item.reorder_point) return 'low';
  return 'ok';
}

const STATUS_ORDER: Record<InvStatus, number> = {
  oos: 0,
  low: 1,
  unconfigured: 2,
  ok: 3,
};

const STATUS_LABEL: Record<InvStatus, string> = {
  ok: 'OK',
  low: 'Low',
  oos: 'OOS',
  unconfigured: 'Unconfigured',
};

const STATUS_VARIANT: Record<
  InvStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  ok: 'default',
  low: 'secondary',
  oos: 'destructive',
  unconfigured: 'outline',
};

export function InventoryHealth() {
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [search, setSearch] = useState('');
  const [configuring, setConfiguring] = useState<InventoryItem | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['popilot', 'inventory'],
    queryFn: () => fetchInventory(),
  });

  if (isLoading) return <LoadingState rows={6} />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const items = data?.items ?? [];

  const filtered = items.filter((i) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return i.sku.toLowerCase().includes(q) || (i.title ?? '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'sku') {
      cmp = a.sku.localeCompare(b.sku);
    } else if (sortKey === 'on_hand') {
      cmp = a.on_hand - b.on_hand;
    } else if (sortKey === 'velocity_7d') {
      cmp = a.velocity_7d - b.velocity_7d;
    } else if (sortKey === 'reorder_point') {
      cmp = (a.reorder_point ?? -1) - (b.reorder_point ?? -1);
    } else if (sortKey === 'status') {
      cmp = STATUS_ORDER[inventoryStatus(a)] - STATUS_ORDER[inventoryStatus(b)];
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const oosCount = items.filter((i) => inventoryStatus(i) === 'oos').length;
  const lowCount = items.filter((i) => inventoryStatus(i) === 'low').length;
  const uncfgCount = items.filter((i) => inventoryStatus(i) === 'unconfigured').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Inventory Health</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          SKU-level status across all connected stores. Sort by column header;
          click <strong>Configure</strong> to set reorder thresholds.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SmallStat label="Total SKUs" value={items.length} />
        <SmallStat
          label="Out of stock"
          value={oosCount}
          variant={oosCount > 0 ? 'danger' : undefined}
        />
        <SmallStat
          label="Below reorder"
          value={lowCount}
          variant={lowCount > 0 ? 'warn' : undefined}
        />
        <SmallStat label="Unconfigured" value={uncfgCount} />
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search SKU or title…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {sorted.length} of {items.length} SKUs
            </span>
          </div>

          {sorted.length === 0 ? (
            <EmptyState
              icon={<Boxes className="h-10 w-10" />}
              title="No inventory items found"
              description={search ? 'Try a different search term.' : 'No SKUs have synced yet.'}
            />
          ) : (
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader sortKey="sku" current={sortKey} dir={sortDir} onToggle={toggleSort}>
                      SKU
                    </SortableHeader>
                    <TableHead>Title</TableHead>
                    <SortableHeader sortKey="status" current={sortKey} dir={sortDir} onToggle={toggleSort}>
                      Status
                    </SortableHeader>
                    <SortableHeader sortKey="on_hand" current={sortKey} dir={sortDir} onToggle={toggleSort}>
                      On Hand
                    </SortableHeader>
                    <SortableHeader sortKey="reorder_point" current={sortKey} dir={sortDir} onToggle={toggleSort}>
                      Reorder Pt
                    </SortableHeader>
                    <TableHead>Reorder Qty</TableHead>
                    <SortableHeader sortKey="velocity_7d" current={sortKey} dir={sortDir} onToggle={toggleSort}>
                      Vel 7d
                    </SortableHeader>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((item) => {
                    const st = inventoryStatus(item);
                    return (
                      <TableRow key={`${item.integration_id}:${item.sku}`}>
                        <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                        <TableCell>{item.title ?? <Muted />}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[st]}>{STATUS_LABEL[st]}</Badge>
                        </TableCell>
                        <TableCell className="tabular-nums">{item.on_hand}</TableCell>
                        <TableCell className="tabular-nums">
                          {item.reorder_point ?? <Muted />}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {item.reorder_qty ?? <Muted />}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {item.velocity_7d.toFixed(1)}
                        </TableCell>
                        <TableCell>{item.supplier_name ?? <Muted />}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => setConfiguring(item)}>
                            Configure
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfigureDialog
        item={configuring}
        onClose={() => setConfiguring(null)}
      />
    </div>
  );
}

function Muted() {
  return <span className="text-muted-foreground">—</span>;
}

function SortableHeader({
  sortKey,
  current,
  dir,
  onToggle,
  children,
}: {
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onToggle: (k: SortKey) => void;
  children: React.ReactNode;
}) {
  const active = current === sortKey;
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium hover:text-foreground',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {children}
        <Icon className="h-3 w-3" />
      </button>
    </TableHead>
  );
}

// ─── Configure reorder dialog ─────────────────────────────────────
//
// InventoryRow carries integration_id+sku but not the po_products.id
// needed by PATCH /api/v1/products/:id. We look it up via
// GET /api/v1/products?integration_id=&sku_match. Phase 10 follow-up
// can add product_id to InventoryRow directly to avoid the extra round-trip.
function ConfigureDialog({
  item,
  onClose,
}: {
  item: InventoryItem | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={item != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        {item && <ConfigureForm key={`${item.integration_id}:${item.sku}`} item={item} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function ConfigureForm({
  item,
  onClose,
}: {
  item: InventoryItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reorderPoint, setReorderPoint] = useState(
    item.reorder_point != null ? String(item.reorder_point) : '',
  );
  const [reorderQty, setReorderQty] = useState(
    item.reorder_qty != null ? String(item.reorder_qty) : '',
  );
  const [leadTime, setLeadTime] = useState(
    item.lead_time_days > 0 ? String(item.lead_time_days) : '',
  );

  const { data: productsData } = useQuery({
    queryKey: ['popilot', 'products', item.integration_id],
    queryFn: () => fetchProducts({ integration_id: item.integration_id }),
  });
  const product = productsData?.products.find((p) => p.sku === item.sku);

  const mut = useMutation({
    mutationFn: () => {
      if (!product) throw new Error('product record not loaded yet — please wait');
      const patch: Parameters<typeof updateProduct>[1] = {};
      if (reorderPoint !== '') patch.reorder_point = Number(reorderPoint);
      if (reorderQty !== '') patch.reorder_qty = Number(reorderQty);
      if (leadTime !== '') patch.lead_time_days = Number(leadTime);
      return updateProduct(product.id, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['popilot', 'inventory'] });
      qc.invalidateQueries({ queryKey: ['popilot', 'products', item.integration_id] });
      onClose();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="space-y-4"
    >
      <DialogHeader>
        <DialogTitle>
          Configure <span className="font-mono text-base">{item.sku}</span>
        </DialogTitle>
        <DialogDescription>
          Set reorder thresholds. Leave a field blank to keep its current value.
        </DialogDescription>
      </DialogHeader>

      {mut.error && (
        <p className="text-sm text-destructive">{(mut.error as Error).message}</p>
      )}
      {!product && productsData && (
        <p className="rounded-md bg-yellow-500/10 p-3 text-sm text-yellow-700 dark:text-yellow-300">
          SKU not found in products list. It may not be synced yet.
        </p>
      )}

      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="reorder-point">Reorder Point (units)</Label>
          <Input
            id="reorder-point"
            type="number"
            min="0"
            value={reorderPoint}
            onChange={(e) => setReorderPoint(e.target.value)}
            placeholder="e.g. 20"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="reorder-qty">Reorder Quantity (units)</Label>
          <Input
            id="reorder-qty"
            type="number"
            min="1"
            value={reorderQty}
            onChange={(e) => setReorderQty(e.target.value)}
            placeholder="e.g. 100"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="lead-time">Lead Time (days)</Label>
          <Input
            id="lead-time"
            type="number"
            min="1"
            value={leadTime}
            onChange={(e) => setLeadTime(e.target.value)}
            placeholder="e.g. 14"
          />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={mut.isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={mut.isPending || (!product && !!productsData)}
          aria-busy={mut.isPending}
        >
          {mut.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}

function SmallStat({
  label,
  value,
  variant,
}: {
  label: string;
  value: number;
  variant?: 'danger' | 'warn';
}) {
  const valueClass =
    variant === 'danger'
      ? 'text-destructive'
      : variant === 'warn'
        ? 'text-yellow-600 dark:text-yellow-400'
        : 'text-foreground';
  return (
    <Card className={cn(
      variant === 'danger' && value > 0 && 'border-destructive/30',
      variant === 'warn' && value > 0 && 'border-yellow-500/30',
    )}>
      <CardHeader className="pb-2">
        <CardDescription className="text-xs uppercase tracking-wide">
          {label}
        </CardDescription>
        <CardTitle className={cn('text-3xl tabular-nums', valueClass)}>
          {value}
        </CardTitle>
      </CardHeader>
    </Card>
  );
}
